import ExportButton from '../../components/ExportButton';
import WarehouseNameTooltip from '../../components/WarehouseNameTooltip';
import usePageViewState from '../../components/usePageViewState';
import { appendReportSummary, downloadWorkbook } from '../../utils/excelExport';
import { usePermissions } from '../../auth/permissions';
import '../../components/warehouse-filter.css';
import usePersistentFilters, { validateFilters } from '../../components/usePersistentFilters';
import Toast from '../../components/Toast';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import CompactBreadcrumb from '../../components/CompactBreadcrumb';
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Box, Boxes, Check, ChevronDown, ChevronLeft, ChevronRight, Eye, MoreHorizontal, ListChecks, House, Package, Pencil, Plus, RotateCcw, Scale, Search, Trash2, Users, Warehouse } from 'lucide-react';
import { createInventory, deleteInventory, getInventory, getInventorySummary, listInventory, updateInventory } from './inventoryService';
import { CustomerLogo } from '../../components/CustomerModal';
import InventoryModal from './InventoryModal';
import InventoryInlineRows from './InventoryInlineRows';
import { cargoSummary, cartonMeasurement, inventoryRows } from '../customers/customerInventory';
import '../warehouses/warehouses.css';
import '../customers/customers.css';
import './inventory.css';

const PAGE_SIZE = 15;
const ACTION_MENU_WIDTH = 140;
const ACTION_MENU_HEIGHT = 112;
const emptyFilters = { query: '', warehouse_id: '', customer_id: '' };
const emptyLookups = { warehouses: [], customers: [], products: [], packaging_types: [], priorities: [], units: [], statuses: ['Draft', 'Planned', 'Available', 'Reserved', 'Completed'] };
const emptySummary = { warehouse_capacity: 0, free_capacity: 0, total_cartons: 0, total_products: 0, net_weight: 0, gross_weight: 0 };

function positionActionMenu(anchor) {
  if (!anchor) return { top: 0, left: 0 };
  const rect = anchor.getBoundingClientRect();
  const tableLeft = anchor.closest('table')?.getBoundingClientRect().left ?? 8;
  const left = Math.min(Math.max(8, tableLeft, rect.left), window.innerWidth - ACTION_MENU_WIDTH - 8);
  const below = rect.bottom + 6;
  const top = below + ACTION_MENU_HEIGHT > window.innerHeight - 8
    ? Math.max(8, rect.top - ACTION_MENU_HEIGHT - 6)
    : below;
  return { top, left };
}

function formatCount(value, suffix) {
  const number = Number(value) || 0;
  return `${number.toLocaleString('en-US')}${suffix ? ` ${suffix}` : ''}`;
}

function formatWeight(value) {
  const number = Number(value) || 0;
  const formatted = Number.isInteger(number) ? number.toLocaleString('en-US') : number.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return formatted;
}

function splitWarehouseName(name) {
  const match = String(name || '').match(/^(.*?)(?:\s*\(([^)]+)\))\s*$/);
  if (match) return { title: match[1].trim(), code: match[2] };
  return { title: name || '—', code: '' };
}

function typeTone(name) {
  const key = String(name || '').toLowerCase();
  if (key.includes('pallet')) return 'pallet';
  if (key.includes('carton')) return 'carton';
  if (key.includes('wood')) return 'wooden';
  return 'other';
}

function filterOptionText(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (!React.isValidElement(node)) return '';
  return React.Children.toArray(node.props.children).map(filterOptionText).join(' ');
}

function FilterMenu({ open, onToggle, onClose, icon, label, value, className = '', children }) {
  const ref = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [showAllOptions, setShowAllOptions] = useState(false);
  useEffect(() => { if (!open) setShowAllOptions(false); }, [open]);
  const searchable = className.includes('warehouse-filter') || className.includes('customer-filter');
  const options = searchable ? React.Children.toArray(children).filter(option =>
    filterOptionText(option).toLowerCase().includes(query.trim().toLowerCase())) : children;
  useEffect(() => {
    if (!open) return undefined;
    function handlePointer(event) {
      if (!ref.current?.contains(event.target)) onClose();
    }
    function handleKey(event) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && searchable) {
      setQuery('');
      searchRef.current?.focus({ preventScroll: true });
    }
  }, [open, searchable]);

  function handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      searchRef.current?.blur();
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !open) return;
    event.preventDefault();
    const items = Array.from(ref.current?.querySelectorAll('[role="option"]') || []);
    if (!items.length) return;
    const active = items.indexOf(document.activeElement);
    const next = active < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1) :
      (active + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next].focus();
  }

  return <div className={`wh-menu ${className}`} ref={ref} onClick={event => {
    if (searchable && !event.target.closest('input')) searchRef.current?.focus({ preventScroll: true });
  }}>
    {searchable ? <div className={`wh-menu-trigger${open ? ' open' : ''}`} onKeyDown={handleKeyDown}>
      {icon}
      <span className="wh-menu-label">{label}</span>
      <input ref={searchRef} className="wh-menu-search-input" type="search" role="combobox" aria-label={`${label} filter`} aria-controls={listId}
        aria-haspopup="listbox" aria-expanded={open} aria-autocomplete="list" autoComplete="off"
        value={open ? query : value} placeholder={open ? `Search ${label.toLowerCase()}...` : undefined}
        onFocus={() => { if (!open) { setQuery(''); onToggle(); } }}
        onClick={() => { if (!open) { setQuery(''); onToggle(); } }}
        onChange={event => { setQuery(event.target.value); if (!open) onToggle(); }} />
      <button type="button" tabIndex={-1} className="dropdown-list-arrow" aria-label={`Show all ${label.toLowerCase()} options`} aria-haspopup="listbox" aria-expanded={open} aria-controls={listId} onMouseDown={event => event.preventDefault()} onClick={() => { setQuery(''); setShowAllOptions(true); if (!open) onToggle(); searchRef.current?.focus({ preventScroll: true }); }}><ChevronDown size={14} strokeWidth={1.8} className="wh-menu-caret" /></button>
    </div> : <button className={`wh-menu-trigger${open ? ' open' : ''}`} type="button" aria-haspopup="listbox" aria-expanded={open} onClick={onToggle}>
      {icon}
      <span className="wh-menu-label">{label}</span>
      <span className="wh-menu-value">{value}</span>
      <ChevronDown size={14} strokeWidth={1.8} className="wh-menu-caret" />
    </button>}
    {open && (!searchable || showAllOptions || query.trim()) && <div className="wh-menu-panel" id={listId} role="listbox">{options}{searchable && !options.length && <p className="wh-menu-empty">No matching {label.toLowerCase()}.</p>}</div>}
  </div>;
}

async function exportPdf(rows, format = 'pdf') {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Inventory', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  const headers = ['Date', 'Reference', 'Warehouse', 'Customer', 'Marko', 'Type', 'Qty', 'Carton Qty', 'Product', 'Prod Qty', 'Prod Gross (kg)', 'Prod Net (kg)', 'Carton Gross (kg)', 'Carton Net (kg)', 'Carton CBM', 'Gross Wt (kg)', 'Net Wt (kg)', 'Total CBM'];
  const widths = [22, 28, 32, 38, 16, 16, 12, 14, 36, 14, 16, 16, 16, 16, 16, 16, 16, 16];
  const columnOrder = [1, 2, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 3, 4, 5];
  const columnIndex = original => columnOrder.indexOf(original) + 1;
  const numericColumns = new Set([7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(columnIndex));
  sheet.columns = columnOrder.map(column => ({ header: headers[column - 1], width: widths[column - 1] }));
  function styleRow(row, background, bold = false) {
    row.height = 20;
    for (let column = 1; column <= headers.length; column += 1) {
      const cell = row.getCell(column);
      cell.font = { name: 'Calibri', size: 11, bold, color: { argb: 'FF17345F' } };
      cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: bold || [3, 4, 9].map(columnIndex).includes(column) };
      cell.fill = background
        ? { type: 'pattern', pattern: 'solid', fgColor: { argb: background } }
        : { type: 'pattern', pattern: 'none' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
      if (numericColumns.has(column)) {
        cell.numFmt = Number.isInteger(Number(cell.value)) ? '#,##0' : '#,##0.##########';
      }
    }
  }
  styleRow(sheet.getRow(1), 'FFE4EDF5', true);
  sheet.getRow(1).height = 24;
  function excelDate(value) {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return value;
    return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  }
  function wrappedLines(value, width) {
    return String(value ?? '').split(/\r?\n/).reduce((total, paragraph) => {
      let lines = 1;
      let used = 0;
      paragraph.split(/\s+/).forEach(word => {
        if (used && used + 1 + word.length > width) { lines += 1; used = 0; }
        if (used) used += 1;
        used += word.length;
        if (used > width) { lines += Math.ceil(used / width) - 1; used = ((used - 1) % width) + 1; }
      });
      return total + lines;
    }, 0);
  }
  rows.forEach(item => {
    const packaging = item.packaging?.length ? item.packaging : [{ ...item, batches: [{ ...item, batch_quantity: item.total_carton_qty, products: item.products || [] }] }];
    const groupedRows = inventoryRows([{ ...item, packaging }]);
    const firstRow = sheet.rowCount + 1;
    const merges = [];
    groupedRows.forEach(({ pack, batch, product, inventorySpan, packSpan, batchSpan }, index) => {
      const packWeight = field => pack.use_master_measurements && Number(pack.rawMeasurements?.[field]) > 0
        ? Number(pack.rawMeasurements[field])
        : pack.batches?.length
          ? pack.measurement_level === 'Product'
            ? pack.batches.reduce((sum, carton) => sum + (carton.products || []).reduce((total, product) => total + (Number(product[field]) || 0), 0), 0)
            : pack.batches.reduce((sum, carton) => sum + (Number(carton[field]) || 0), 0)
          : Number(pack[field]) || 0;
      const row = sheet.getRow(firstRow + index);
      const values = [
        inventorySpan ? excelDate(item.inventory_date) : null,
        inventorySpan ? item.reference_number : null,
        inventorySpan ? item.warehouse_code || item.warehouse_name : null,
        inventorySpan ? item.customer_name : null,
        inventorySpan ? item.marko : null,
        packSpan ? pack.packaging_type : null,
        packSpan ? pack.packaging_quantity : null,
        batchSpan ? (batch?.batch_quantity ?? pack.total_carton_qty) : null,
        product?.name || '', product?.quantity ?? null, product?.gross_weight ?? null, product?.net_weight ?? null,
        ...['gross_weight', 'net_weight', 'cbm'].map(field => batchSpan ? cartonMeasurement(batch, field) : null),
        packSpan ? packWeight('gross_weight') : null,
        packSpan ? packWeight('net_weight') : null,
        packSpan ? packWeight('cbm') : null,
      ];
      row.values = columnOrder.map(column => values[column - 1]);
      styleRow(row);
      row.getCell(1).numFmt = 'dd-mm-yyyy';
      row.height = Math.max(20, wrappedLines(product?.name, widths[8] - 2) * 15 + 4);
      const merge = (columns, span) => {
        if (span <= 1) return;
        columns.forEach(column => merges.push([row.number, columnIndex(column), row.number + span - 1, columnIndex(column)]));
      };
      merge([1, 2, 3, 4, 5], inventorySpan);
      merge([6, 7, 16, 17, 18], packSpan);
      merge([8, 13, 14, 15], batchSpan);
    });
    merges.forEach(range => sheet.mergeCells(...range));
  });
  const summaryRow = appendReportSummary(sheet, { labelColumns: 3, productColumn: columnIndex(9), productCount: cargoSummary(rows).totalProducts, numericColumns });
  styleRow(summaryRow, 'FFE4EDF5', true);
  return downloadWorkbook(workbook, `inventory.${format}`);
}

export default function InventoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const writable = canWrite('Inventory');
  const [records, setRecords] = useState([]);
  const tableRef = useRef(null);
  const [columnSizing, setColumnSizing] = useState({ widths: [84, 180, 90], typeWidth: 64, digits: [72, 48, 100, 72, 124, 112, 112, 104, 104, 124, 124, 104], minimum: 1662 });
  useLayoutEffect(() => {
    const table = tableRef.current;
    const wrapper = table?.parentElement;
    if (!table || !wrapper) return;
    const probe = document.createElement('span');
    Object.assign(probe.style, { position: 'fixed', visibility: 'hidden', whiteSpace: 'nowrap' });
    document.body.appendChild(probe);
    const measure = (cell, text, extra) => {
      const style = getComputedStyle(cell);
      Object.assign(probe.style, {
        fontFamily: style.fontFamily, fontSize: style.fontSize,
        fontWeight: style.fontWeight, fontStyle: style.fontStyle,
        letterSpacing: style.letterSpacing, textTransform: style.textTransform,
      });
      probe.textContent = text;
      return Math.ceil(probe.getBoundingClientRect().width || String(text).length * 6) + extra;
    };
    const base = [0, 100, 80];
    [15, 16].forEach((column, index) => {
      const header = table.querySelector(`thead th:nth-child(${column})`);
      if (header) base[index] = Math.max(base[index], measure(header, header.textContent, 16));
    });
    table.querySelectorAll('td[data-edit-label="Warehouse"] .inv-warehouse > *').forEach(content => {
      base[0] = Math.max(base[0], measure(content, content.textContent, 16));
    });
    table.querySelectorAll('td[data-edit-label="Customer"]').forEach(cell => {
      const name = cell.querySelector('.wh-name') || cell;
      const logo = cell.querySelector('.cu-logo');
      const gap = parseFloat(getComputedStyle(cell.querySelector('.inv-customer') || cell).gap) || 8;
      base[1] = Math.max(base[1], measure(name, name.textContent, 16 + (logo?.getBoundingClientRect().width || 30) + gap));
    });
    // Include cell padding and borders so full names fit without clipping.
    base[0] += 2;
    base[1] += 12;
    table.querySelectorAll('td[data-edit-label^="Product name"]').forEach(cell => {
      base[2] = Math.max(base[2], measure(cell, cell.textContent, 16));
    });
    const selectors = [
      'td[data-edit-label="Marko"]', 'td[data-edit-label^="Package quantity"]',
      'td[data-edit-label^="Carton quantity"]', 'td[data-edit-label^="Product quantity"]',
      'td[data-edit-label^="Product gross_weight"]', 'td[data-edit-label^="Product net_weight"]',
      'td[data-carton-measurement="gross_weight"]', 'td[data-carton-measurement="net_weight"]', 'td[data-carton-measurement="cbm"]',
      'td[data-inventory-weight="gross"]', 'td[data-inventory-weight="net"]', 'td[data-inventory-cbm="total"]',
    ];
    const digits = [17, 3, 4, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((column, index) => {
      const header = table.querySelector(`thead th:nth-child(${column})`);
      let width = header ? measure(header, header.textContent, 18) : 60;
      table.querySelectorAll(selectors[index]).forEach(cell => {
        width = Math.max(width, measure(cell.firstElementChild || cell, cell.textContent, 18));
      });
      return width;
    });
    const typeHeader = table.querySelector('thead th:nth-child(2)');
    let typeWidth = typeHeader ? measure(typeHeader, typeHeader.textContent, 18) : 64;
    table.querySelectorAll('td[data-edit-label^="Packaging type"] .inv-type').forEach(badge => {
      const style = getComputedStyle(badge);
      const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      typeWidth = Math.max(typeWidth, measure(badge, badge.textContent, 18 + padding));
    });
    probe.remove();
    const minimum = 40 + typeWidth + digits.reduce((sum, width) => sum + width, 0) + base.reduce((sum, width) => sum + width, 0);
    const resize = () => {
      const extra = Math.max(0, wrapper.clientWidth - minimum) / 2;
      const widths = base.map((width, index) => width + (index === 0 ? 0 : extra));
      setColumnSizing(previous => previous.minimum === minimum && previous.typeWidth === typeWidth && previous.digits.every((width, index) => width === digits[index]) && previous.widths.every((width, index) => width === widths[index]) ? previous : { widths, typeWidth, digits, minimum });
    };
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    observer?.observe(wrapper);
    window.addEventListener('resize', resize);
    return () => { observer?.disconnect(); window.removeEventListener('resize', resize); };
  }, [records]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(emptySummary);
  const [lookups, setLookups] = useState(emptyLookups);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters, reconcileFilters, filtersReady] = usePersistentFilters('inventoryFilters', emptyFilters, {
    searchParams, setSearchParams: (params, options) => setSearchParams(params, { ...options, state: location.state }), urlKeys: { warehouse_id: 'warehouseId' },
  });
  const bootstrapSummary = useRef(null);
  const inventoryLoaded = useRef(false);
  const [page, setPage] = useState(1);
  const [openMenu, setOpenMenu] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [inventoryModal, setInventoryModal] = usePageViewState('inventoryModal', null);
  const [inlineRecord, setInlineRecord] = useState(null);
  const [inlinePreview, setInlinePreview] = useState(null);
  const inlineEditor = useRef(null);
  async function saveInlineInventory(id, payload) {
    if (!writable) throw new Error('You do not have permission to edit inventory.');
    await updateInventory(id, payload);
    await loadPage(filters, currentPage, { background: true });
    setInlinePreview(null);
    if (inventoryModal?.mode === 'view') {
      const detail = await getInventory(id, { background: true });
      setInventoryModal({ mode: 'view', item: detail });
    }
    showToast('Inventory updated.');
  }
  useEffect(() => {
    if (inventoryModal?.mode === 'view') {
      const content = document.querySelector('.app-content');
      if (content) content.scrollTop = 0;
    }
  }, [inventoryModal?.mode, inventoryModal?.item?.id]);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const actionMenuRef = useRef(null);
  const toastTimer = useRef(null);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = total ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(currentPage * PAGE_SIZE, total);
  const preview = inlineRecord && inlinePreview?.id === inlineRecord.id ? inlinePreview : null;
  const displayedSummary = preview ? {
    ...summary,
    gross_weight: summary.gross_weight + preview.gross,
    net_weight: summary.net_weight + preview.net,
    free_capacity: summary.free_capacity - preview.volume,
    total_cartons: summary.total_cartons + preview.cartons,
    total_products: summary.total_products + preview.products,
  } : summary;
  const kpis = [
    [Warehouse, 'blue', 'Warehouse Capacity', formatCount(summary.warehouse_capacity, 'm³')],
    [Box, 'green', 'Free Capacity', formatCount(displayedSummary.free_capacity, 'm³')],
    [Package, 'amber', 'Total Cartons', formatCount(displayedSummary.total_cartons, 'ctns')],
    [Boxes, 'orange', 'Total Products', formatCount(displayedSummary.total_products, 'SKUs')],
    [Scale, 'cyan', 'Net Weight', formatCount(displayedSummary.net_weight, 'kg')],
    [Scale, 'teal', 'Gross Weight', formatCount(displayedSummary.gross_weight, 'kg')],
  ];

  const applyLoaded = useCallback(async (inventory, count, data) => {
    if (data.lookups) {
      const options = { ...emptyLookups, ...data.lookups };
      setLookups(options);
      if (reconcileFilters(values => validateFilters(values, emptyFilters, {
        warehouse_id: ['', ...options.warehouses.map(row => row.id)],
        customer_id: options.customers.map(row => row.id),
      }, true))) return;
    }
    setRecords(inventory);
    setTotal(count);
    setSummary({
      warehouse_capacity: data.warehouse_capacity || 0,
      free_capacity: data.free_capacity || 0,
      total_cartons: data.total_cartons || 0,
      total_products: data.total_products || 0,
      net_weight: data.net_weight || 0,
      gross_weight: data.gross_weight || 0,
    });
    if (data.lookups) setLookups({ ...emptyLookups, ...data.lookups });
  }, [reconcileFilters]);

  async function loadPage(nextFilters = filters, nextPage = currentPage, options = {}) {
    const [{ inventory, total: count }, data] = await Promise.all([
      listInventory({ ...nextFilters, page: nextPage, pageSize: PAGE_SIZE }, options),
      getInventorySummary(nextFilters, 'Inventory', options),
    ]);
    await applyLoaded(inventory, count, data);
  }

  // Resolve saved/URL warehouse preferences using lookups before requesting rows.
  useEffect(() => {
    if (filtersReady) return undefined;
    setPage(1);
    let cancelled = false;
    getInventorySummary(filters).then(async data => {
      if (cancelled) return;
      bootstrapSummary.current = { filters, data };
      await applyLoaded([], 0, data);
      if (!data.lookups) reconcileFilters(values => values);
    }).catch(error => { if (!cancelled) showToast(error.message, 'error'); });
    return () => { cancelled = true; };
  }, [filters, filtersReady, applyLoaded, reconcileFilters]);

  useEffect(() => {
    if (!filtersReady) return undefined;
    let cancelled = false;
    const initialSummary = bootstrapSummary.current;
    bootstrapSummary.current = null;
    async function load() {
      try {
        const [{ inventory, total: count }, data] = await Promise.all([
          listInventory({ ...filters, page, pageSize: PAGE_SIZE }, { background: inventoryLoaded.current }),
          initialSummary && JSON.stringify(initialSummary.filters) === JSON.stringify(filters)
            ? Promise.resolve(initialSummary.data) : getInventorySummary(filters, 'Inventory', { background: inventoryLoaded.current }),
        ]);
        if (cancelled) return;
        await applyLoaded(inventory, count, data);
        inventoryLoaded.current = true;
      } catch (error) {
        if (!cancelled) {
          setRecords([]);
          setTotal(0);
          showToast(error.message, 'error');
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [filters, page, applyLoaded, filtersReady]);

  useEffect(() => {
    setActionMenu(null);
  }, [filters, currentPage]);

  useEffect(() => {
    if (!deleteItem) return undefined;
    function handleKey(event) {
      if (event.key === 'Escape' && !deleting) closeDelete();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [deleteItem, deleting]);

  useEffect(() => {
    if (!actionMenu) return undefined;
    function updatePosition() {
      setMenuPos(positionActionMenu(actionMenu.anchor));
    }
    function handlePointer(event) {
      if (actionMenuRef.current?.contains(event.target) || actionMenu.anchor?.contains(event.target)) return;
      setActionMenu(null);
    }
    function handleKey(event) {
      if (event.key === 'Escape') {
        actionMenu.anchor?.focus();
        setActionMenu(null);
      }
    }
    updatePosition();
    actionMenuRef.current?.querySelector('[role="menuitem"]')?.focus();
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [actionMenu]);

  useEffect(() => () => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
  }, []);

  function showToast(message, tone = 'success', title) {
    setToast({ message, tone, title });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4000);
  }

  function updateFilter(name, value) {
    setFilters(current => ({ ...current, [name]: value }));
    setPage(1);
    setOpenMenu(null);
    setActionMenu(null);
  }

  function closeMenus() {
    setOpenMenu(null);
  }

  function findRecord(id) {
    return records.find(item => item.id === id && !item.is_deleted) || null;
  }

  function toggleActionMenu(event, item) {
    event.stopPropagation();
    if (actionMenu?.id === item.id) {
      setActionMenu(null);
      return;
    }
    const anchor = event.currentTarget;
    setActionMenu({ id: item.id, anchor });
    setMenuPos(positionActionMenu(anchor));
  }

  function openAdd() {
    if (!writable) return;
    setActionMenu(null);
    setInventoryModal({ mode: 'add', item: null });
  }

  async function openRecord(item) {
    setActionMenu(null);
    try {
      const detail = await getInventory(item.id);
      if (detail) setInventoryModal({ mode: 'view', item: detail });
    } catch (error) { showToast(error.message, 'error'); }
  }

  async function openEdit(record) {
    if (!writable) return;
    const selected = record?.id ? record : findRecord(actionMenu?.id);
    setActionMenu(null);
    setInlineRecord(null);
    if (!selected) return;
    try {
      const detail = await getInventory(selected.id);
      setInventoryModal({ mode: 'edit', item: detail || selected });
    } catch (error) {
      setInventoryModal({ mode: 'edit', item: selected });
    }
  }

  function openDelete() {
    if (!writable) return;
    const selected = findRecord(actionMenu?.id);
    setActionMenu(null);
    if (selected) setDeleteItem(selected);
  }

  function closeDelete() {
    if (deleting) return;
    setDeleteItem(null);
  }

  async function saveInventory(payload) {
    if (!writable) return;
    if (inventoryModal?.mode === 'edit' && inventoryModal.item) {
      try {
        await updateInventory(inventoryModal.item.id, payload);
      } catch (error) {
        if (error.status === 403 || error.status === 404) {
          setInventoryModal(null);
          showToast("You don't have permission to edit this inventory record.", 'error');
          return;
        }
        throw error;
      }
      await loadPage(filters, currentPage);
      showToast('Inventory updated.');
      return;
    }
    const saved = await createInventory(payload);
    if (!saved?.id) throw new Error('Inventory was not saved to the database.');
    if (page !== 1) setPage(1);
    else await loadPage(filters, 1);
    showToast('Inventory created.');
  }

  async function confirmDelete() {
    if (!writable) return;
    if (deleting || !deleteItem) return;
    setDeleting(true);
    try {
      await deleteInventory(deleteItem.id);
      setDeleteItem(null);
      await loadPage(filters, currentPage);
      showToast('Inventory deleted.');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setDeleteItem(null);
      setDeleting(false);
    }
  }

  const selectedWarehouse = lookups.warehouses.find(row => String(row.id) === String(filters.warehouse_id));
  const selectedCustomer = lookups.customers.find(row => String(row.id) === String(filters.customer_id));

  if (inventoryModal?.mode === 'view') return <>
    <InventoryModal
      key={`view-${inventoryModal.item.id}`}
      mode="view"
      item={inventoryModal.item}
      lookups={lookups}
      onClose={() => setInventoryModal(null)}
      onEdit={writable ? () => setInventoryModal(current => ({ ...current, mode: 'edit', fromDetails: true })) : undefined}
      onSubmit={payload => saveInlineInventory(inventoryModal.item.id, payload)}
      onToast={showToast}
    />
    <Toast toast={toast} onClose={() => setToast(null)} />
  </>;

  return <section className="wh-page inv-page inv-list-page">
    <div className="wh-title">
      <div>
        {location.state?.fromWarehouses && <CompactBreadcrumb parent="Warehouses" current="Inventory" onNavigate={() => navigate('/warehouses')} />}
        <h1>Inventory</h1>
      </div>
      <div className="wh-title-actions">
        {!inlineRecord && <><ExportButton onExport={format => exportPdf(records, format).catch(error => showToast(error.message || 'Unable to export inventory.', 'error'))}>Export</ExportButton>
        <button className="wh-add" type="button" disabled={!writable} onClick={openAdd}><Plus size={14} />Add Inventory</button></>}
      </div>
    </div>
    <div className="wh-kpis inv-kpis">
      {kpis.map(([Icon, tone, label, value]) => <article className="wh-kpi" key={label}>
        <span className={`wh-kpi-icon ${tone}`}><Icon size={16} strokeWidth={1.8} /></span>
        <div className="wh-kpi-copy">
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      </article>)}
    </div>
    <div className="wh-panel">
      <div className="wh-filters inv-filters">
      <label className="wh-search"><Search size={14} /><input type="search" placeholder="Search inventory..." value={filters.query} onChange={event => updateFilter('query', event.target.value)} /></label>
      {!location.state?.fromWarehouses && <>
      <FilterMenu className="warehouse-filter" open={openMenu === 'warehouse'} onToggle={() => { setActionMenu(null); setOpenMenu(openMenu === 'warehouse' ? null : 'warehouse'); }} onClose={closeMenus} icon={<House size={14} strokeWidth={1.8} />} label="Warehouse" value={selectedWarehouse?.name || 'Select Warehouse'}>
        {lookups.warehouses.map(row => {
            const selected = String(filters.warehouse_id || '') === String(row.id || '');
            return <button key={row.id || 'all'} className={`wh-menu-item${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => updateFilter('warehouse_id', row.id || '')}>
              <span>{row.name}</span>
              {selected && <Check size={14} strokeWidth={2} />}
            </button>;
          })}
      </FilterMenu>
      <FilterMenu className="customer-filter" open={openMenu === 'customer'} onToggle={() => { setActionMenu(null); setOpenMenu(openMenu === 'customer' ? null : 'customer'); }} onClose={closeMenus} icon={<Users size={14} strokeWidth={1.8} />} label="Customer" value={selectedCustomer?.name || 'All Customers'}>
        {[{ id: '', name: 'All Customers' }, ...lookups.customers].map(row => {
            const selected = String(filters.customer_id || '') === String(row.id || '');
            return <button key={row.id || 'all'} className={`wh-menu-item${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => updateFilter('customer_id', row.id || '')}>
              <span>{row.name}</span>
              {selected && <Check size={14} strokeWidth={2} />}
            </button>;
          })}
      </FilterMenu>
      <button className="wh-filter-btn" type="button" onClick={() => { setFilters(emptyFilters); setPage(1); setOpenMenu(null); setActionMenu(null); }}><RotateCcw size={14} strokeWidth={1.7} />Reset</button>
      </>}
    </div>
      <div className="table-scroll-wrapper">
        <table className="wh-table inv-table" ref={tableRef} style={{ '--inventory-min-width': `${columnSizing.minimum}px`, minWidth: columnSizing.minimum }}>
          <colgroup>
            <col style={{ width: 40 }} />
            <col style={{ width: columnSizing.typeWidth }} />
            <col style={{ width: columnSizing.digits[1] }} />
            <col style={{ width: columnSizing.digits[2] }} />
            <col style={{ width: columnSizing.widths[2] }} />
            <col style={{ width: columnSizing.digits[3] }} />
            <col style={{ width: columnSizing.digits[4] }} />
            <col style={{ width: columnSizing.digits[5] }} />
            <col style={{ width: columnSizing.digits[6] }} />
            <col style={{ width: columnSizing.digits[7] }} />
            <col style={{ width: columnSizing.digits[8] }} />
            {[9, 10, 11].map(index => <col key={index} style={{ width: columnSizing.digits[index] }} />)}
            <col style={{ width: columnSizing.widths[0] }} />
            <col style={{ width: columnSizing.widths[1] }} />
            <col style={{ width: columnSizing.digits[0] }} />
          </colgroup>
          <thead>
            <tr>
              <th aria-label="Actions" title="Actions"><ListChecks size={16} aria-hidden="true" /></th>
              <th>Type</th>
              <th>Qty</th>
              <th>Carton Qty</th>
              <th>Product</th>
              <th>Prod Qty</th>
              <th>Prod Gross</th>
              <th>Prod Net</th>
              <th>Carton Gross</th>
              <th>Carton Net</th>
              <th>Carton CBM</th>
              <th>Gross Wt</th>
              <th>Net Wt</th>
              <th>Total CBM</th>
              <th>Warehouse</th>
              <th>Customer</th>
              <th>Marko</th>
            </tr>
          </thead>
          <tbody>
            {records.map(item => {
              if (inlineRecord?.id === item.id) return <InventoryInlineRows locationColumnsLast showCartonMeasurements ref={inlineEditor} onPreview={setInlinePreview} initialCell={inlineRecord.initialCell} key={`inline-${item.id}`} item={inlineRecord} lookups={lookups} onSave={payload => saveInlineInventory(item.id, payload)} onCancel={() => setInlineRecord(null)} onToast={showToast} />;
              const packaging = item.packaging?.length ? item.packaging : [{ ...item, batches: [{ ...item, batch_quantity: item.total_carton_qty, products: item.products }] }];
              const rows = inventoryRows([{ ...item, packaging }]);
              const warehouse = splitWarehouseName(item.warehouse_code || item.warehouse_name);
              const tableRows = rows.map(({ key, pack, batch, product: savedProduct, inventorySpan, packSpan, batchSpan }, rowIndex) => {
                const product = savedProduct || { name: '—', quantity: '', gross_weight: '', net_weight: '' };
                const packWeight = field => pack.use_master_measurements && Number(pack.rawMeasurements?.[field]) > 0
                  ? Number(pack.rawMeasurements[field])
                  : pack.batches?.length
                    ? pack.measurement_level === 'Product'
                      ? pack.batches.reduce((sum, carton) => sum + (carton.products || []).reduce((total, product) => total + (Number(product[field]) || 0), 0), 0)
                      : pack.batches.reduce((sum, carton) => sum + (Number(carton[field]) || 0), 0)
                    : Number(pack[field]) || 0;
                return (
                <tr key={`${key}-${rowIndex}`} className="inv-record-row" onClick={async event => {
                  if (!writable || event.target.closest('button')) return;
                  const cell = event.target.closest('td');
                  if (!cell?.dataset.editLabel) return;
                  try { const detail = await getInventory(item.id, { background: true }); setInlineRecord({ ...detail, initialCell: cell.dataset.editLabel }); }
                  catch (error) { showToast(error.message, 'error'); }
                }}>
                  {inventorySpan > 0 && <td className="inv-actions-cell" rowSpan={inventorySpan} onClick={event => event.stopPropagation()}>
                    <button type="button" className="inv-icon-action" title="Actions" aria-label={`Actions for inventory ${item.reference_number || item.id}`} aria-haspopup="menu" aria-expanded={actionMenu?.id === item.id} aria-controls={actionMenu?.id === item.id ? 'inv-row-action-menu' : undefined} onClick={event => toggleActionMenu(event, item)}><MoreHorizontal size={17} /></button>
                  </td>}
                  {packSpan > 0 && <td rowSpan={packSpan} data-edit-label={`Packaging type ${packaging.indexOf(pack) + 1}`}><span className={`inv-type ${typeTone(pack.packaging_type)}`}>{pack.packaging_type || '—'}</span></td>}
                  {packSpan > 0 && <td rowSpan={packSpan} data-edit-label={`Package quantity ${packaging.indexOf(pack) + 1}`}>{pack.packaging_quantity || '—'}</td>}
                  {batchSpan > 0 && <td rowSpan={batchSpan} data-edit-label={`Carton quantity ${packaging.indexOf(pack) + 1}-${pack.batches.indexOf(batch) + 1}`}>{(batch?.batch_quantity ?? pack.total_carton_qty) || '—'}</td>}
                  <td data-edit-label={`Product name ${rowIndex + 1}`}>{product.name || '—'}</td>
                  <td data-edit-label={`Product quantity ${rowIndex + 1}`}>{product.quantity === '' ? '—' : formatWeight(product.quantity)}</td>
                  <td data-edit-label={`Product gross_weight ${rowIndex + 1}`}>{product.gross_weight === '' ? '—' : formatWeight(product.gross_weight)}</td>
                  <td data-edit-label={`Product net_weight ${rowIndex + 1}`}>{product.net_weight === '' ? '—' : formatWeight(product.net_weight)}</td>
                  {batchSpan > 0 && ['gross_weight', 'net_weight', 'cbm'].map(field => <td key={field} data-carton-measurement={field} rowSpan={batchSpan}>{Number(batch?.[field] || 0).toLocaleString('en-US', { maximumFractionDigits: field === 'cbm' ? 3 : 2 })}</td>)}
                  {packSpan > 0 && <td data-inventory-weight="gross" data-edit-label={`Packaging gross_weight ${packaging.indexOf(pack) + 1}`} rowSpan={packSpan}>{formatWeight(packWeight('gross_weight'))}</td>}
                  {packSpan > 0 && <td data-inventory-weight="net" data-edit-label={`Packaging net_weight ${packaging.indexOf(pack) + 1}`} rowSpan={packSpan}>{formatWeight(packWeight('net_weight'))}</td>}
                  {packSpan > 0 && <td data-inventory-cbm="total" data-edit-label={`Packaging cbm ${packaging.indexOf(pack) + 1}`} rowSpan={packSpan}>{packWeight('cbm').toLocaleString('en-US', { maximumFractionDigits: 3 })}</td>}
                  {inventorySpan > 0 && <td rowSpan={inventorySpan} data-edit-label="Warehouse">
                    <WarehouseNameTooltip name={item.warehouse_name}><div className="inv-warehouse">
                      <span>{warehouse.title}</span>
                      {warehouse.code ? <small>{warehouse.code}</small> : item.warehouse_city ? <small>{item.warehouse_city}</small> : null}
                    </div></WarehouseNameTooltip>
                  </td>}
                  {inventorySpan > 0 && <td rowSpan={inventorySpan} data-edit-label="Customer">
                    <div className="inv-customer">
                      <CustomerLogo name={item.customer_name} logo={item.customer_logo} />
                      <span className="wh-name">{item.customer_name}</span>
                    </div>
                  </td>}
                  {inventorySpan > 0 && <td rowSpan={inventorySpan} data-edit-label="Marko">{item.marko ? <span className="inv-marko">{item.marko}</span> : '—'}</td>}
                </tr>
              );
              });
              return tableRows;
            })}
          </tbody>
        </table>
      </div>
      {!records.length && <p className="inv-empty">No inventory found.</p>}
      <div className="wh-pager">
        <p>Showing {start}–{end} of {total} Inventory Records</p>
        <div>
          <button type="button" disabled={currentPage === 1} onClick={() => { setActionMenu(null); setPage(currentPage - 1); }}><ChevronLeft size={14} />Previous</button>
          {Array.from({ length: pageCount }, (_, index) => <button key={index + 1} type="button" className={`wh-page-number${currentPage === index + 1 ? ' current' : ''}`} aria-current={currentPage === index + 1 ? 'page' : undefined} onClick={() => { setActionMenu(null); setPage(index + 1); }}>{index + 1}</button>)}
          <button type="button" disabled={currentPage === pageCount} onClick={() => { setActionMenu(null); setPage(currentPage + 1); }}>Next<ChevronRight size={14} /></button>
        </div>
      </div>
    </div>

    {actionMenu && <div
      id="inv-row-action-menu"
      className="wh-action-menu"
      role="menu"
      ref={actionMenuRef}
      style={{ top: menuPos.top, left: menuPos.left }}
    >
      <button className="wh-action-item" type="button" role="menuitem" onClick={() => {
        const selected = findRecord(actionMenu.id);
        setActionMenu(null);
        if (selected) openRecord(selected);
      }}><Eye size={14} />View</button>
      <button className="wh-action-item" type="button" role="menuitem" disabled={!writable} onClick={openEdit}>
        <Pencil size={14} strokeWidth={1.8} />Edit
      </button>
      <button className="wh-action-item danger" type="button" role="menuitem" disabled={!writable} onClick={openDelete}>
        <Trash2 size={14} strokeWidth={1.8} />Delete
      </button>
    </div>}

    {inventoryModal && (writable || inventoryModal.mode === 'view') && <InventoryModal
      key={`${inventoryModal.mode}-${inventoryModal.item?.id || 'new'}`}
      onEdit={writable ? () => setInventoryModal(current => ({ ...current, mode: 'edit', fromDetails: true })) : undefined}
      onBackToDetails={inventoryModal.fromDetails ? () => setInventoryModal(current => ({ ...current, mode: 'view' })) : undefined}
      mode={inventoryModal.mode}
      item={inventoryModal.item}
      lookups={lookups}
      onClose={() => setInventoryModal(null)}
      onSubmit={saveInventory}
      onToast={showToast}
      canCreateCustomer={canWrite('Customers')}
    />}

    {writable && deleteItem && <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeDelete(); }}>
      <div className="wh-modal wh-modal-confirm" role="dialog" aria-modal="true" aria-labelledby="inv-delete-title">
        <h2 id="inv-delete-title">Delete Inventory?</h2>
        <p className="wh-modal-copy">Are you sure you want to delete this inventory record?</p>
        <div className="wh-delete-target">
          <strong>{deleteItem.warehouse_name}</strong>
          <span>{deleteItem.customer_name}{deleteItem.reference_number ? ` · ${deleteItem.reference_number}` : ''}</span>
        </div>
        <div className="wh-modal-actions">
          <button className="wh-btn-ghost" type="button" onClick={closeDelete} disabled={deleting} autoFocus>Cancel</button>
          <button className="wh-btn-danger" type="button" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
        </div>
      </div>
    </div>}

    <Toast toast={toast} onClose={() => setToast(null)} />
  </section>;
}
